import React, { useContext, useState, useEffect, useRef, useMemo, useCallback } from "react";
import { X, Search } from "lucide-react";
import AppContext from "../../context/AppContext";
import translations from "../../components/common/Translations";
import { getScreens, getRolePermissions, updateRolePermissions } from "../../service/Role";
import { toast } from "../../utils/toast";
import { Spinner } from "../../Spinner";
import { isRTL } from "../../utils/rtl";
import CommonButton from "../../components/common/CommonButton";

const getBackendErrorMessage = (error) =>
  error?.response?.data?.body?.value ??
  error?.response?.data?.body?.error ??
  error?.response?.data?.message ??
  error?.body?.value ??
  error?.body?.error ??
  error?.message;

const blankPermission = () => ({
  canView: false,
  canCreate: false,
  canUpdate: false,
  canDelete: false,
  canBulkReAssign: false,
  canBulkPriorityUpdate: false,
});

// ---------------- screen grouping (frontend-only, presentational) ----------------
// getScreens() carries no group/category field, and no such mapping exists anywhere
// else in the app -- this is a purely local, static lookup used only to organize the
// rail/sections visually. Any screenCode not listed here (including future/unknown
// screens) safely falls back to the "other" group below instead of being hidden.
const SCREEN_GROUPS = {
  USER_MANAGEMENT: "access",
  ROLE_MANAGEMENT: "access",
  TASK_MANAGEMENT: "operations",
  WORKFLOW: "operations",
  CONFIGURATION: "operations",
  ASSIGNMENT_RULES: "operations",
  SPECIAL_ASSIGNMENT_RULES: "operations",
  METER_INVENTORY: "assets",
  REPORTS: "insights",
  ACTIVITY_DASHBOARD: "insights",
  ACCOUNT_HISTORY: "insights",
  PDF_REPORT: "insights",
  SETTINGS: "system",
  ACCOUNT_PROTECTION: "system",
  INSTALMENT_PLAN: "system",
  DCRC_CONFIG: "system",
};

const GROUPS = [
  { id: "access", colorClass: "bg-[#236DB4]", labelKey: "permGroupAccess" },
  { id: "operations", colorClass: "bg-[#0E9F8E]", labelKey: "permGroupOperations" },
  { id: "assets", colorClass: "bg-[#B5730B]", labelKey: "permGroupAssets" },
  { id: "insights", colorClass: "bg-[#7C5CFC]", labelKey: "permGroupInsights" },
  { id: "system", colorClass: "bg-[#E4587A]", labelKey: "permGroupSystem" },
  { id: "other", colorClass: "bg-[#667085]", labelKey: "permGroupOther" },
];

// The matrix columns come from each screen's `supportedActions` (GET /api/v1/screens),
// so a screen only offers the actions the backend actually implements for it -- e.g.
// BULK_REASSIGN exists on TASK_MANAGEMENT only.
const ACTION_DEFS = {
  VIEW: { uiKey: "view", permKey: "canView" },
  CREATE: { uiKey: "create", permKey: "canCreate" },
  UPDATE: { uiKey: "update", permKey: "canUpdate" },
  DELETE: { uiKey: "delete", permKey: "canDelete" },
  BULK_REASSIGN: { uiKey: "bulkReAssign", permKey: "canBulkReAssign" },
  BULK_PRIORITY_UPDATE: { uiKey: "bulkPriorityUpdate", permKey: "canBulkPriorityUpdate" },
};

// The four core actions render as the toggle grid; anything else (BULK_REASSIGN today)
// renders in the dashed row underneath it.
const GRID_ACTIONS = ["VIEW", "CREATE", "UPDATE", "DELETE"];

// Screens served by an older backend carry no supportedActions; they keep the previous
// four-column behaviour rather than rendering an empty row.
const DEFAULT_ACTIONS = GRID_ACTIONS;

// Actions the backend still lists for a screen but the role matrix must not offer.
// Inventory has no bulk re-assign flow, so the toggle is hidden there -- and because
// saving only keeps supported actions, any old grant is cleared on the next save.
const HIDDEN_ACTIONS = {
  INVENTORY_MANAGEMENT: ["BULK_REASSIGN"],
};

const actionsForScreen = (screen) => {
  const raw =
    Array.isArray(screen?.supportedActions) && screen.supportedActions.length > 0
      ? screen.supportedActions
      : DEFAULT_ACTIONS;
  const hidden = HIDDEN_ACTIONS[String(screen?.code ?? "").toUpperCase()] || [];
  const seen = new Set();
  return raw
    .map((action) => String(action).toUpperCase())
    .filter((action) => {
      if (!ACTION_DEFS[action] || seen.has(action) || hidden.includes(action)) return false;
      seen.add(action);
      return true;
    })
    .map((action) => ({ action, ...ACTION_DEFS[action] }));
};

const gridActionsForScreen = (screen) =>
  actionsForScreen(screen).filter(({ action }) => GRID_ACTIONS.includes(action));

const extraActionsForScreen = (screen) =>
  actionsForScreen(screen).filter(({ action }) => !GRID_ACTIONS.includes(action));

const RING_CIRC = 2 * Math.PI * 15.5;

// iOS-style toggle switch built from the same `role="switch"` button + inner
// thumb span idiom already used across the app (e.g. EditProject.js's DCRC
// toggles) instead of a hidden checkbox + custom CSS track. All Tailwind
// classes below are complete literal strings (never string-concatenated) so
// the JIT class scanner can find and generate every one of them.
const SwitchButton = ({ checked, disabled, onChange, isRtl, offTrackClass, size = "md", ariaLabel }) => {
  const isSmall = size === "sm";
  const trackSizeClass = isSmall ? "h-[19px] w-[32px]" : "h-[22px] w-[38px]";
  const thumbSizeClass = isSmall ? "h-[13px] w-[13px]" : "h-4 w-4";
  const thumbRestSideClass = isRtl ? "right-[3px]" : "left-[3px]";
  const thumbOnClass = isSmall
    ? (isRtl ? "-translate-x-[13px]" : "translate-x-[13px]")
    : (isRtl ? "-translate-x-4" : "translate-x-4");

  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={() => !disabled && onChange(!checked)}
      className={`relative inline-flex shrink-0 rounded-full transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-blue-500 ${trackSizeClass} ${disabled ? "cursor-not-allowed opacity-[0.55]" : "cursor-pointer"} ${checked ? "bg-[#236DB4]" : offTrackClass}`}
    >
      <span
        className={`pointer-events-none absolute top-[3px] ${thumbRestSideClass} ${thumbSizeClass} rounded-full bg-white shadow-[0_1px_2px_rgba(0,0,0,0.25)] transition-transform duration-150 ${checked ? thumbOnClass : "translate-x-0"}`}
      />
    </button>
  );
};

const PermissionModal = ({ open, onClose, role, onSaved }) => {
  const { theme, langMode } = useContext(AppContext);
  const isRtl = isRTL(langMode);
  const [screensList, setScreensList] = useState([]);
  const [screenPermissions, setScreenPermissions] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [query, setQuery] = useState("");
  const [activeCat, setActiveCat] = useState(null);
  const contentRef = useRef(null);
  const sectionRefs = useRef({});
  // Screen catalog is static reference data, not role-specific -- cache it
  // across modal opens instead of re-fetching for every role.
  const screensCacheRef = useRef(null);

  const roleId = role?.id ?? role?.roleId;
  const roleName = role?.name ?? role?.roleName ?? "Role";
  const modalTitle = `${translations[langMode]?.permissions ?? "Permissions"} · ${roleName}`;

  useEffect(() => {
    if (!open || !roleId) return;
    const load = async () => {
      setLoadingData(true);
      try {
        // Load screens list (cached after first fetch -- static reference data)
        let screensArray;
        if (screensCacheRef.current) {
          screensArray = screensCacheRef.current;
        } else {
          const screens = await getScreens();
          screensArray = Array.isArray(screens) ? screens : [];
          screensCacheRef.current = screensArray;
        }
        setScreensList(screensArray);

        // Load role permissions
        let permsList = [];
        try {
          const permsResponse = await getRolePermissions(roleId);
          permsList = Array.isArray(permsResponse) ? permsResponse : [];
        } catch (e) {
          console.warn("Failed to load role permissions:", e);
          permsList = [];
        }

        // Initialize permissions object: start with all screens set to false
        const permsObj = {};
        screensArray.forEach((screen) => {
          if (screen.id != null) {
            permsObj[screen.id] = blankPermission();
          }
        });

        // Map permissions from API response to screens by screenId
        permsList.forEach((perm) => {
          const screenId = perm.screenId;
          if (screenId != null && permsObj[screenId] != null) {
            permsObj[screenId] = {
              canView: Boolean(perm.canView),
              canCreate: Boolean(perm.canCreate),
              canUpdate: Boolean(perm.canUpdate),
              canDelete: Boolean(perm.canDelete),
              canBulkReAssign: Boolean(perm.canBulkReAssign),
              canBulkPriorityUpdate: Boolean(perm.canBulkPriorityUpdate),
            };
          }
        });

        setScreenPermissions(permsObj);
      } catch (err) {
        console.error(err);
        const backendMessage = getBackendErrorMessage(err);
        if (backendMessage) {
          toast.error(backendMessage);
        }
        onClose();
      } finally {
        setLoadingData(false);
      }
    };
    load();
  }, [open, roleId, onClose, langMode]);

  // reset transient UI state (search, active rail item) whenever a different role is opened
  useEffect(() => {
    if (open) {
      setQuery("");
      setActiveCat(null);
    }
  }, [open, roleId]);

  // close on Escape
  useEffect(() => {
    if (!open) return;
    function onKey(e) { if (e.key === "Escape") onClose(); }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const q = query.trim().toLowerCase();
  const matchesQuery = useCallback(
    (screen) =>
      !q ||
      String(screen.name || "").toLowerCase().includes(q) ||
      String(screen.description || "").toLowerCase().includes(q),
    [q]
  );

  const screensByGroup = useMemo(() => {
    const map = {};
    GROUPS.forEach((g) => { map[g.id] = []; });
    screensList.forEach((screen) => {
      const gid = SCREEN_GROUPS[screen.code] || "other";
      if (!map[gid]) map[gid] = [];
      map[gid].push(screen);
    });
    return map;
  }, [screensList]);

  const visibleGroups = useMemo(
    () => GROUPS.filter((g) => (screensByGroup[g.id] || []).some(matchesQuery)),
    [screensByGroup, matchesQuery]
  );

  // default / repair the active rail selection once screens are available
  useEffect(() => {
    if (!open || screensList.length === 0) return;
    if (activeCat == null || !screensList.some((s) => s.id === activeCat)) {
      setActiveCat(screensList[0].id);
    }
  }, [open, screensList, activeCat]);

  // scrollspy: highlight active rail item as the panel scrolls
  useEffect(() => {
    if (!open) return;
    const root = contentRef.current;
    if (!root) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveCat(Number(entry.target.dataset.screenId));
        });
      },
      { root, threshold: 0, rootMargin: "-10% 0px -75% 0px" }
    );
    Object.values(sectionRefs.current).forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, [open, query, screensList]);

  const granted = useMemo(() => {
    let n = 0;
    screensList.forEach((screen) => {
      const p = screenPermissions[screen.id];
      if (!p) return;
      actionsForScreen(screen).forEach(({ permKey }) => {
        if (p[permKey]) n++;
      });
    });
    return n;
  }, [screensList, screenPermissions]);

  const totalFields = useMemo(
    () => screensList.reduce((sum, screen) => sum + actionsForScreen(screen).length, 0),
    [screensList]
  );

  const pct = totalFields ? granted / totalFields : 0;

  function togglePermission(screenId, permKey) {
    setScreenPermissions((prev) => {
      const current = prev[screenId] || blankPermission();
      const newValue = !current[permKey];
      const next = { ...current, [permKey]: newValue };
      if (permKey === "canView" && !newValue) {
        next.canCreate = false;
        next.canUpdate = false;
        next.canDelete = false;
        next.canBulkReAssign = false;
        next.canBulkPriorityUpdate = false;
      }
      return { ...prev, [screenId]: next };
    });
  }

  // "All" covers exactly the actions this screen supports, so the header switch and the
  // n/total counter next to it always agree.
  function toggleAllForScreen(screen, value) {
    setScreenPermissions((prev) => {
      const current = prev[screen.id] || blankPermission();
      const next = { ...current };
      actionsForScreen(screen).forEach(({ permKey }) => {
        next[permKey] = value;
      });
      if (!value) next.canBulkReAssign = false;
      return { ...prev, [screen.id]: next };
    });
  }

  function scrollToScreen(screenId) {
    sectionRefs.current[screenId]?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  const handleSave = async () => {
    if (!roleId) return;
    setLoading(true);
    try {
      const permissions = screensList
        .map((screen) => {
          const perms = screenPermissions[screen.id] || blankPermission();
          // An action the screen does not support can never be granted, even if a
          // stale permission row from the backend had it set.
          const supported = new Set(actionsForScreen(screen).map(({ permKey }) => permKey));
          const granted = (permKey) => supported.has(permKey) && Boolean(perms[permKey]);
          return {
            screenId: Number(screen.id),
            screenCode: String(screen.code ?? ""),
            screenName: String(screen.name ?? ""),
            canView: granted("canView"),
            canCreate: granted("canCreate"),
            canUpdate: granted("canUpdate"),
            canDelete: granted("canDelete"),
            canBulkReAssign: granted("canBulkReAssign"),
            canBulkPriorityUpdate: granted("canBulkPriorityUpdate"),
          };
        })
        .filter(
          (p) =>
            p.canView || p.canCreate || p.canUpdate || p.canDelete || p.canBulkReAssign || p.canBulkPriorityUpdate
        );
      const payload = { permissions };
      const data = await updateRolePermissions(roleId, payload);
      if (data?.header?.code === 600 || data?.id != null) {
        toast.success("Permissions updated successfully");
        onSaved?.();
        onClose();
      } else {
        const backendMessage =
          data?.body?.value ??
          data?.body?.error ??
          data?.header?.message;
        if (backendMessage) {
          toast.error(backendMessage);
        }
      }
    } catch (err) {
      const backendMessage = getBackendErrorMessage(err);
      if (backendMessage) {
        toast.error(backendMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  // ---------------- theme-aware Tailwind class fragments ----------------
  // Mirrors this app's existing .dark-mode (#0b0d10 bg / #e4d6d6 text) and
  // .high-contrast (#F5F5F5 bg / #1A1A1A text) palettes at the granularity
  // the original design needed, since neither of those global classes cascade
  // styling to descendants on their own (only the element bearing the class).
  const isDark = theme === "dark";
  const isHighContrast = theme === "high-contrast";
  const modalSurface = isDark
    ? "bg-[#0b0d10] text-[#e4d6d6]"
    : isHighContrast
      ? "bg-[#F5F5F5] text-[#1A1A1A]"
      : "bg-white text-[#101828]";
  const borderTone = isDark ? "border-[#2a2d33]" : isHighContrast ? "border-[#1A1A1A]" : "border-[#E4E8EF]";
  const mutedTone = isDark ? "text-[#9aa0aa]" : "text-[#667085]";
  const closeBtnTone = isDark
    ? "bg-[#16181c] border-[#2a2d33] text-[#9aa0aa] hover:bg-[#1f2126] hover:text-[#e4d6d6]"
    : isHighContrast
      ? "bg-white border-[#1A1A1A] text-[#1A1A1A] hover:bg-[#F1F4F8] hover:text-[#101828]"
      : "bg-white border-[#E4E8EF] text-[#667085] hover:bg-[#F1F4F8] hover:text-[#101828]";
  const searchTone = isDark
    ? "bg-[#16181c] border-[#2a2d33]"
    : isHighContrast
      ? "bg-white border-[#1A1A1A]"
      : "bg-[#F1F4F8] border-[#E4E8EF]";
  const searchInputTextTone = isDark ? "text-[#e4d6d6]" : isHighContrast ? "text-[#1A1A1A]" : "text-[#101828]";
  const progressTone = isDark
    ? "bg-[#132433] border-[#1e3a53]"
    : isHighContrast
      ? "bg-white border-[#1A1A1A]"
      : "bg-[#EAF2FB] border-[#D7E7F6]";
  const progressTextTone = isDark ? "text-[#7fb4e8]" : isHighContrast ? "text-[#1A1A1A]" : "text-[#1A5A96]";
  const progressMutedTone = isDark ? "text-[#9aa0aa]" : "text-[#344054]";
  const railItemTextTone = isDark ? "text-[#c7cbd1]" : isHighContrast ? "text-[#1A1A1A]" : "text-[#344054]";
  const railItemHoverBg = isDark ? "hover:bg-[#16181c]" : "hover:bg-[#F1F4F8]";
  const railItemActiveBg = isDark ? "bg-[#132433]" : isHighContrast ? "bg-[#E4E8EF]" : "bg-[#EAF2FB]";
  const railItemActiveText = isDark ? "text-[#7fb4e8]" : isHighContrast ? "text-[#1A1A1A]" : "text-[#1A5A96]";
  const groupHeadingBg = isDark
    ? "bg-[linear-gradient(#0b0d10_70%,transparent)]"
    : isHighContrast
      ? "bg-[linear-gradient(#F5F5F5_70%,transparent)]"
      : "bg-[linear-gradient(#fff_70%,transparent)]";
  const sectionHeadBg = isDark ? "bg-[#101216]" : isHighContrast ? "bg-white" : "bg-[#FBFCFE]";
  const sectionTitleTextTone = isDark ? "text-[#e4d6d6]" : isHighContrast ? "text-[#1A1A1A]" : "text-[#101828]";
  const sectionDescTone = isDark ? "text-[#9aa0aa]" : "text-[#667085]";
  const toggleCellLabelTone = isDark ? "text-[#c7cbd1]" : "text-[#344054]";
  const extraRowLabelTone = isDark ? "text-[#e4d6d6]" : "text-[#101828]";
  const switchTrackOffTone = isDark ? "bg-[#3a3d44]" : isHighContrast ? "bg-[#8a8a8a]" : "bg-[#D0D6E1]";
  const railBorderSide = isRtl ? "border-l" : "border-r";
  const toggleCellAlign = isRtl ? "items-end" : "items-start";
  const textAlignClass = isRtl ? "text-right" : "text-left";

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-5 bg-[rgba(14,21,38,0.5)] backdrop-blur-[2px]"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className={`relative flex h-[88vh] w-[96vw] max-w-[960px] flex-col rounded-[18px] shadow-[0_24px_60px_rgba(14,21,38,0.22),0_4px_16px_rgba(14,21,38,0.12)] ${modalSurface} ${textAlignClass}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="pmTitle"
        dir={isRtl ? "rtl" : "ltr"}
      >
        {/* header */}
        <div className={`border-b px-[22px] pb-3.5 pt-[18px] ${borderTone}`}>
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 id="pmTitle" className="m-0 mb-1 flex items-center gap-[9px] text-lg font-extrabold">
                {modalTitle}
              </h2>
              <p className={`m-0 text-[12.5px] ${mutedTone}`}>
                {translations[langMode]?.permissionModalSubtitle ?? "Choose what this role can view, create, update or delete across your workspace."}
              </p>
            </div>
            <button
              onClick={onClose}
              aria-label="Close"
              className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-[9px] border transition-colors ${closeBtnTone}`}
            >
              <X size={15} />
            </button>
          </div>

          {!loadingData && screensList.length > 0 && (
            <div className="mt-3.5 flex flex-wrap items-center gap-3">
              <div className={`flex min-w-[200px] flex-1 items-center gap-2 rounded-[10px] border px-3 py-2 ${searchTone}`}>
                <Search size={14} className="shrink-0 text-[#98A2B3]" />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={translations[langMode]?.search ?? "Search"}
                  className={`w-full border-none bg-transparent text-[13px] outline-none ${searchInputTextTone}`}
                />
              </div>
              <div className={`flex shrink-0 items-center gap-2.5 rounded-[10px] border px-3 py-[7px] ${progressTone}`}>
                <svg width="26" height="26" viewBox="0 0 36 36">
                  <circle cx="18" cy="18" r="15.5" fill="none" stroke="#D7E7F6" strokeWidth="4" />
                  <circle
                    cx="18" cy="18" r="15.5" fill="none" stroke="#236DB4" strokeWidth="4"
                    strokeLinecap="round"
                    strokeDasharray={RING_CIRC}
                    strokeDashoffset={RING_CIRC * (1 - pct)}
                    transform="rotate(-90 18 18)"
                    style={{ transition: "stroke-dashoffset 170ms" }}
                  />
                </svg>
                <span className={`whitespace-nowrap text-[12.5px] font-bold ${progressTextTone}`}>
                  {granted} <span className={`font-semibold ${progressMutedTone}`}>{translations[langMode]?.of || "of"} {totalFields} {translations[langMode]?.granted || "granted"}</span>
                </span>
              </div>
            </div>
          )}
        </div>

        {/* body: rail + content */}
        {loadingData ? (
          <div className="flex flex-1 items-center justify-center py-8">
            <Spinner />
          </div>
        ) : screensList.length === 0 ? (
          <div className="flex flex-1 items-center justify-center py-8">
            <p className="text-sm text-[#667085]">{translations[langMode]?.noScreensAvailable ?? "No screens available"}</p>
          </div>
        ) : (
          <div className="flex min-h-0 flex-1 max-[900px]:flex-col">
            <nav
              className={`w-[250px] shrink-0 overflow-y-auto p-3 scrollbar-container task-list-scrollbar max-[900px]:flex max-[900px]:w-full max-[900px]:flex-none max-[900px]:flex-row max-[900px]:items-center max-[900px]:gap-3 max-[900px]:overflow-x-auto max-[900px]:overflow-y-hidden max-[900px]:border-b max-[900px]:border-l-0 max-[900px]:border-r-0 max-[900px]:px-3.5 max-[900px]:py-2.5 ${railBorderSide} ${borderTone}`}
              aria-label="Permission screen categories"
            >
              {visibleGroups.map((g) => {
                const screens = (screensByGroup[g.id] || []).filter(matchesQuery);
                if (screens.length === 0) return null;
                return (
                  <div key={g.id} className="max-[900px]:flex max-[900px]:shrink-0 max-[900px]:items-center max-[900px]:gap-1">
                    <div className="flex items-center gap-1.5 px-2.5 pb-1.5 pt-1.5 max-[900px]:shrink-0 max-[900px]:whitespace-nowrap max-[900px]:pb-1.5 text-[10.5px] font-extrabold uppercase tracking-[0.07em] text-[#98A2B3]">
                      <span className={`inline-block h-[7px] w-[7px] shrink-0 rounded-full ${g.colorClass}`} />
                      {translations[langMode]?.[g.labelKey] ?? g.id}
                    </div>
                    {screens.map((screen) => {
                      const perms = screenPermissions[screen.id] || blankPermission();
                      const screenActions = actionsForScreen(screen);
                      const g2 = screenActions.reduce(
                        (n, { permKey }) => n + (perms[permKey] ? 1 : 0),
                        0
                      );
                      const t2 = screenActions.length;
                      const isActive = activeCat === screen.id;
                      const isFull = g2 === t2;
                      return (
                        <button
                          key={screen.id}
                          onClick={() => scrollToScreen(screen.id)}
                          className={`flex w-full items-center justify-between gap-2 rounded-[9px] border-none bg-transparent px-2.5 py-2 text-[13px] max-[900px]:w-auto max-[900px]:shrink-0 max-[900px]:whitespace-nowrap font-semibold transition-colors duration-150 ${
                            isActive ? `${railItemActiveBg} ${railItemActiveText}` : `${railItemTextTone} ${railItemHoverBg}`
                          }`}
                        >
                          <span className="truncate">{screen.name}</span>
                          <span className={`shrink-0 text-[11px] font-bold ${isActive ? "text-[#1A5A96]" : isFull ? "text-[#0E9F8E]" : "text-[#98A2B3]"}`}>
                            {g2}/{t2}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                );
              })}
            </nav>

            <div
              className="min-h-0 flex-1 overflow-y-auto px-6 pb-6 pt-2 max-[900px]:px-3.5 scrollbar-container task-list-scrollbar"
              style={{ scrollBehavior: "smooth" }}
              ref={contentRef}
            >
              {visibleGroups.map((g) => {
                const screens = (screensByGroup[g.id] || []).filter(matchesQuery);
                if (screens.length === 0) return null;
                return (
                  <div key={g.id}>
                    <div className={`sticky top-0 z-[2] flex items-center gap-[7px] px-0 pb-2 pt-4 text-[11px] font-extrabold uppercase tracking-[0.07em] text-[#98A2B3] ${groupHeadingBg}`}>
                      <span className={`inline-block h-[7px] w-[7px] shrink-0 rounded-full ${g.colorClass}`} />
                      {translations[langMode]?.[g.labelKey] ?? g.id}
                    </div>
                    {screens.map((screen) => {
                      const perms = screenPermissions[screen.id] || blankPermission();
                      const isViewEnabled = perms.canView;
                      const gridActions = gridActionsForScreen(screen);
                      const extraActions = extraActionsForScreen(screen);
                      const allOn = actionsForScreen(screen).every(({ permKey }) => perms[permKey]);
                      return (
                        <section
                          key={screen.id}
                          ref={(el) => (sectionRefs.current[screen.id] = el)}
                          data-screen-id={screen.id}
                          className={`mb-3 scroll-mt-14 overflow-hidden rounded-xl border ${borderTone}`}
                        >
                          <div className={`flex items-center justify-between gap-3 border-b px-4 py-[13px] ${sectionHeadBg} ${borderTone}`}>
                            <div className="flex min-w-0 items-center gap-2.5">
                              <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-[9px] text-xs font-extrabold text-white ${g.colorClass}`}>
                                {String(screen.name || "").split(" ").filter(Boolean).map((w) => w[0]).slice(0, 2).join("")}
                              </div>
                              <div>
                                <h4 className={`m-0 text-[13.5px] font-bold ${sectionTitleTextTone}`}>{screen.name}</h4>
                                {screen.description && <p className={`m-0 mt-px text-xs ${sectionDescTone}`}>{screen.description}</p>}
                              </div>
                            </div>
                            <label className="flex shrink-0 items-center gap-2 text-[11.5px] font-bold text-[#667085]">
                              {translations[langMode]?.all ?? "All"}
                              <SwitchButton
                                checked={allOn}
                                onChange={(value) => toggleAllForScreen(screen, value)}
                                isRtl={isRtl}
                                size="sm"
                                offTrackClass={switchTrackOffTone}
                                ariaLabel={translations[langMode]?.all ?? "All"}
                              />
                            </label>
                          </div>

                          <div className="grid grid-cols-4 gap-0 px-4 pb-4 pt-3.5 max-[900px]:grid-cols-2 max-[900px]:gap-y-3">
                            {gridActions.map(({ uiKey, permKey }) => {
                              const disabled = permKey !== "canView" && !isViewEnabled;
                              return (
                                <div key={uiKey} className={`flex flex-col gap-2 ${toggleCellAlign}`}>
                                  <SwitchButton
                                    checked={!!perms[permKey]}
                                    disabled={disabled}
                                    onChange={() => togglePermission(screen.id, permKey)}
                                    isRtl={isRtl}
                                    offTrackClass={switchTrackOffTone}
                                    ariaLabel={translations[langMode][uiKey] || uiKey}
                                  />
                                  <span className={`text-[11.5px] font-bold ${toggleCellLabelTone}`}>
                                    {translations[langMode][uiKey] || uiKey[0].toUpperCase() + uiKey.slice(1)}
                                  </span>
                                </div>
                              );
                            })}
                          </div>

                          {extraActions.map(({ uiKey, permKey }) => {
                            const label =
                              translations[langMode]?.[uiKey] ||
                              uiKey[0].toUpperCase() + uiKey.slice(1);
                            return (
                              <div
                                key={uiKey}
                                className={`mt-0.5 flex items-center justify-between gap-3 border-t border-dashed px-4 pb-3.5 pt-2.5 ${borderTone}`}
                              >
                                <span className={`block text-[12.5px] font-bold ${extraRowLabelTone}`}>
                                  {label}
                                </span>
                                <SwitchButton
                                  checked={!!perms[permKey]}
                                  disabled={!isViewEnabled}
                                  onChange={() => togglePermission(screen.id, permKey)}
                                  isRtl={isRtl}
                                  offTrackClass={switchTrackOffTone}
                                  ariaLabel={label}
                                />
                              </div>
                            );
                          })}
                        </section>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* footer */}
        <div className={`flex flex-wrap items-center justify-between gap-3 border-t px-[22px] py-3.5 ${borderTone}`}>
          <span className={`text-xs ${mutedTone}`}>{translations[langMode]?.changesApplyOnceSaved ?? "Changes apply once you save."}</span>
          <div className="flex gap-2.5">
            <CommonButton colorIntent="danger" variant="danger" onClick={onClose}>{translations[langMode].cancle || "Cancel"}</CommonButton>
            <CommonButton colorIntent="success" variant="primary" onClick={handleSave} disabled={loading || loadingData}>
              {translations[langMode].save}
            </CommonButton>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PermissionModal;
